import React, { useContext } from 'react';
import { LinkedInIcon } from './icons/LinkedInIcon';
import { InstagramIcon } from './icons/InstagramIcon';
import { LanguageContext, useNavigation } from './LanguageContext';

const Footer: React.FC = () => {
  const { t } = useContext(LanguageContext);
  const { path, navigate } = useNavigation();
  const quickLinks = t.footer?.quickLinks?.links || [];

  const handleInternalLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (!href) return;

    if (href.startsWith('#')) {
      const id = href.substring(1);
      const scrollToSection = () => {
        const element = document.getElementById(id);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      };

      if (path !== '/') {
        navigate('/');
        setTimeout(scrollToSection, 100);
      } else {
        scrollToSection();
      }
    } else {
      navigate(href);
    }
  };

  const handleBrandClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (!href) return;
    navigate(href);
  };

  return (
    <footer className="bg-black text-white relative z-10">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-sm font-semibold tracking-wider uppercase text-gray-400">{t.footer?.quickLinks?.title}</h3>
            <ul className="mt-4 space-y-2">
              {quickLinks.map((link: { name: string, href: string }, index: number) => {
                const hashHref = link?.href?.startsWith('#') ? link.href : `#${link.href || '/'}`;
                return <li key={link?.href || index}><a href={hashHref} onClick={(e) => handleInternalLinkClick(e, link?.href)} className="text-base text-gray-300 hover:text-white">{link?.name || ''}</a></li>
              })}
            </ul>
          </div>
          <div className="md:col-span-3">
            <h3 className="text-sm font-semibold tracking-wider uppercase text-gray-400">{t.footer?.sisterBrands?.title}</h3>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-4">
              {(t.footer?.sisterBrands?.brands || []).map((brand: { name: string, href: string }, index: number) => {
                const brandHref = brand?.href || '/';
                return <a key={brand?.name || index} href={brandHref} onClick={(e) => handleBrandClick(e, brand?.href)} className="text-base font-medium text-gray-300 hover:text-white transition-colors">{brand?.name || ''}</a>
              })}
            </div>
            <div className="mt-8 border-t border-gray-700 pt-8">
              <h3 className="text-sm font-semibold tracking-wider uppercase text-gray-400">{t.footer?.contactUs?.title}</h3>
              <div className="mt-4 space-y-2 text-gray-300">
                <p>
                  <a href={`mailto:${t.footer?.contactUs?.email}`} className="hover:text-white">{t.footer?.contactUs?.email}</a>
                </p>
                <p>
                  {t.footer?.contactUs?.whatsappUAE?.label}: <a href={t.footer?.contactUs?.whatsappUAE?.href} data-contact-region="uae" target="_blank" rel="noopener noreferrer" className="hover:text-white">{t.footer?.contactUs?.whatsappUAE?.number}</a>
                </p>
                <p>
                  {t.footer?.contactUs?.whatsappEU?.label}: <a href={t.footer?.contactUs?.whatsappEU?.href} data-contact-region="eu" target="_blank" rel="noopener noreferrer" className="hover:text-white">{t.footer?.contactUs?.whatsappEU?.number}</a>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 border-t border-gray-700 pt-8 flex flex-col sm:flex-row items-center justify-between">
          <p className="text-base text-gray-400">&copy; {t.footer?.copyrightYear} Buildhub GLOBAL. {t.footer?.rights}</p>
          <div className="flex gap-x-6 mt-4 sm:mt-0">
            <a href="https://www.linkedin.com/company/buildhub-global" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
              <span className="sr-only">LinkedIn</span>
              <LinkedInIcon />
            </a>
            <a href="https://www.instagram.com/buildhub.global" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
              <span className="sr-only">Instagram</span>
              <InstagramIcon />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
